import {CanCloseDeskFeat, Desk, UserActiveDeskFeat} from "lib/commons/desk";
import {EventsMgr, IEvents, IListeners} from "lib/commons/events";
import {AuthEndPoint, IEndPoint, IO, PublicEndPoint} from "lib/commons/io/io";
import {DOM} from "lib/commons/xml/dom";
import {ChainUniverse, OChainUniverseConfig} from "lib/wsp/chain";
import {SRC, srcRef} from "lib/wsp/src";
import {JWspUriChangeMsg, WSP, Wsp} from "lib/wsp/wsp";
import {IDatasEditor} from "lib/wsp/wspMetaUi";
import {EWspChangesEvts, WspsLivePlace} from "lib/wsp/wspsLive";

/**
 * Implantation ACTUELLEMENT EN STAND BY d'un desk intégré directement dans
 * un interactive generator gérant l'édition dans les pages.
 */

declare global {
	interface Window {
		pageUpdatesMgr: IPageUpdatesMgr
		commentsBackend: ICommentsBackend
	}
}

window.desk = UserActiveDeskFeat.add(CanCloseDeskFeat.add(new Desk()));

/** Pour mettre à dispo les classes utilitaires pour l'init dans un contexte hors modules. */
export function noModules() {
	(window as any).IO = IO;
	(window as any).AuthEndPoint = AuthEndPoint;
	(window as any).PublicEndPoint = PublicEndPoint;
	return window;
}

/** Initialise l'env d'édition et met à dispo window.pageUpdatesMgr et window.commentsBackend. */
type ODynGenEnvConfig = {
	/** Config de l'univers. */
	universe: OChainUniverseConfig
	/** Code de l'atelier dont cette page est issue. */
	wspCd: string
	/** QuerySelectors spécifiant chaque racine d'analyse des diff avec une version rafraichie de la page. */
	rootsDiffSelectors: string[]
}

export function initEnv(conf: ODynGenEnvConfig) {
	universe = new ChainUniverse(conf.universe);
	place = universe.wspServer.wspsLive.newPlace();
	wsp = place.getWsp(conf.wspCd);
	window.pageUpdatesMgr = pageUpdatesMgr = new PageUpdatesMgr();
	window.commentsBackend = {} as ICommentsBackend;
}

/** Gestionnaire des modifications issues du server et provoque un refresh auto et ciblé de la page en cas de changement. */
export interface IPageUpdatesMgr extends IListeners<IPageUpdatesEvents> {

	inject(webContainer: Element, req: IEndPoint, options?: OPageUpdatesOptions): Promise<void>;

	removeInjectPoint(webContainer: Element): void;
}
export interface IPageUpdatesEvents extends IEvents {

	/**
	 * A la détection du besoin de refresh d'un ou plusieurs containers, permet de réduire la liste
	 * des containers à rafraichir (gestion des dépendances entre containers comme outline et main).
	 * Si null est retourné, le refresh est abandonné.
	 */
	shouldRefresh: (webCtn: Element[]) => Element[];

	/**
	 * Notifié avant la mise à jour de ce container.
	 * Si une Promesse est retournée, le refresh ne sera réalisé qu'après la résolution de la promesse.
	 */
	beforeUpdates: (webCtn: Element) => void | Promise<void>

	/**
	 * Notifié après la mise à jour de chaque container.
	 */
	afterUpdates: (webCtn: Element) => void
}

export interface OPageUpdatesOptions {
	domUpdater: (datas: Document, webContainer: Element) => void;

	/*, format?: IContentType*/
}

/** Backend de modification des commentaires. */
export interface ICommentsBackend {
	startAddComment(oriPath: string, anchor: HTMLElement): IComment | false

	startEditComment(oriPath: string): IComment | false

	startRespondComment(oriPath: string): IComment | false

	deleteComment(oriPath: string): void | false

	closeThread(oriPath: string, close: boolean): void | false
}

export interface IComment {
	oriPath: string

	onCommentMoved(listener: () => void): void

	save(text: string): void

	cancel(): void
}

/** Ajoute des éditeurs dans la page. */
export type OInlineEditorConfig = {
	rootsSelector: string //"div.text,.infoblock_ti>span"
	editor: IDatasEditor
}

export function addEditors(conf: OInlineEditorConfig) {
	new InlineEditors(conf);
}

//type IContentType = 'text/xml' | 'text/html' | 'image/svg+xml';

let universe: ChainUniverse;
let wsp: Wsp;

let place: WspsLivePlace;


class PageUpdatesMgr extends EventsMgr<IPageUpdatesEvents> implements IPageUpdatesMgr {
	updateRoots = new Map<Element, UpdateRoot>();

	constructor() {
		super();

		place.eventsMgr.on("wspUriChange", (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => {
			if (msg.type === EWspChangesEvts.u && msg.wspCd === wsp.code) {
				let toRefresh: Element[];
				for (const updtRoot of this.updateRoots.values()) {
					if (updtRoot.srcRefs.has(msg.srcId || msg.srcUri)) {
						if (!toRefresh) toRefresh = [updtRoot.webCtn];
						else toRefresh.push(updtRoot.webCtn);
					}
				}
				if (!toRefresh) return;
				for (const lstn of this.getListeners('shouldRefresh')) {
					toRefresh = lstn(toRefresh);
					if (!toRefresh) return;
				}
				for (const webCtn of toRefresh) {
					const updt = this.updateRoots.get(webCtn);
					if (updt) updt.refresh();
				}
			}
		});

		place.eventsMgr.on("onConnectionRenewed", async () => {
			//On fusionne la liste des src qui nous intéressent
			const srcRefs = new Set<srcRef>();
			for (const updtRoot of this.updateRoots.values()) {
				for (const srcRef of updtRoot.srcRefs.keys()) srcRefs.add(srcRef);
			}
			const srcs = await WSP.fetchSrcs(wsp, null, Array.from(srcRefs.keys()), [wsp.srcRefField, 'srcDt']);
			let toRefresh: Element[];
			for (const updtRoot of this.updateRoots.values()) {
				for (const src of srcs) {
					const srcDt = updtRoot.srcRefs.get(SRC.srcRef(src));
					if (srcDt && srcDt !== src.srcDt) {
						if (!toRefresh) toRefresh = [updtRoot.webCtn];
						else toRefresh.push(updtRoot.webCtn);
						break;
					}
				}
			}
			if (!toRefresh) return;
			for (const lstn of this.getListeners('shouldRefresh')) {
				toRefresh = lstn(toRefresh);
				if (!toRefresh) return;
			}
			for (const webCtn of toRefresh) {
				const updt = this.updateRoots.get(webCtn);
				if (updt) updt.refresh();
			}
		});
	}

	inject(webCtn: Element, req: IEndPoint, options?: OPageUpdatesOptions): Promise<void> {
		let updateRoot = this.updateRoots.get(webCtn);
		if (!updateRoot) {
			updateRoot = new UpdateRoot(webCtn, options);
			this.updateRoots.set(webCtn, updateRoot);
		}
		return updateRoot.setEndPoint(req/*, format*/);
	}

	removeInjectPoint(webCtn: Element) {
		const updateRoot = this.updateRoots.get(webCtn);
		if (updateRoot) {
			updateRoot.kill();
			this.updateRoots.delete(webCtn);
		}
	}
}


let pageUpdatesMgr: PageUpdatesMgr;

class UpdateRoot {
	refreshEndP: IEndPoint;
	//format?: IContentType;

	srcRefs: Map<srcRef, number> = new Map();

	protected waiting: Promise<void>;

	constructor(public webCtn: Element, readonly options: OPageUpdatesOptions) {
	}

	/** Interrompt intantannément tout traitement, même si refresh async en cours. */
	kill() {
		delete this.webCtn;
	}

	setEndPoint(refreshEndP: IEndPoint/*, format?: IContentType*/): Promise<void> {
		this.refreshEndP = refreshEndP;
		//this.format = format;
		return this.refresh();
	}

	async refresh(): Promise<void> {
		if (this.waiting) return this.waiting;
		const promise = pageUpdatesMgr.emitUntil('beforeUpdates', this.webCtn) as Promise<void>;
		if (promise instanceof Promise) {
			return this.waiting = new Promise<void>(async resolve => {
				await promise;
				this.waiting = null;
				return this.refresh().then(resolve);
			});
		}
		const txt = await this.refreshEndP.fetchText();
		const doc = DOM.parseDom(txt, this.refreshEndP /*, this.format*/);
		const newCtn = doc.body;
		this.srcRefs.clear();
		this.saveDataOrigins(newCtn);
		((this.options && this.options.domUpdater) || defaultUpdater)(doc, this.webCtn);
		pageUpdatesMgr.emit('afterUpdates', this.webCtn);
	}

	protected saveDataOrigins(elt: Element): Element {
		const ori = elt.getAttribute("data-origin");
		if (ori && ori.charAt(0) === '@') {
			const sep = ori.indexOf('?', 1);
			const srcRef = ori.substring(1, sep);
			const sep2 = ori.indexOf('/', sep);
			const srcDt = parseInt(sep2 > 0 ? ori.substring(sep + 1, sep2) : ori.substring(sep + 1));
			this.srcRefs.set(srcRef, srcDt);
		}
		for (let ch = elt.firstElementChild; ch; ch = ch.nextElementSibling) this.saveDataOrigins(ch);
		return elt;
	}
}

function defaultUpdater(newDatas: Document, webCtn: Element): void {
	webCtn.textContent = null;
	const newCtn = newDatas.body;
	while (newCtn.firstChild) webCtn.appendChild(document.adoptNode(newCtn.firstChild));
}

class InlineEditors {

	protected _onEdit: () => void;

	constructor(readonly conf: OInlineEditorConfig) {
		this._onEdit = this.edit.bind(this);
		pageUpdatesMgr.on("afterUpdates", this.onAfterUpdates.bind(this));
	}

	onAfterUpdates(webCtn: Element) {
		const editAnchors = webCtn.querySelectorAll(this.conf.rootsSelector);
		for (let i = 0; i < editAnchors.length; i++) {
			const anchor = editAnchors[i] as IEditorAnchor;
			if (anchor.inlineEditor) continue;
			anchor.inlineEditor = this;
			const btn = anchor.inlineEditorBtn = editAnchors[i].insertBefore(document.createElement("button"), editAnchors[i].firstChild) as HTMLButtonElement;
			btn.title = "Éditer";
			btn.textContent = String.fromCharCode(9997);
			btn.classList.add("wui-edit");
			btn.onclick = this._onEdit;
		}
	}

	edit(ev: MouseEvent) {
		console.log("Edit...");
	}
}

interface IEditorAnchor extends Element {
	inlineEditor: InlineEditors
	inlineEditorBtn: HTMLButtonElement
}
